import { EMPTY_ARRAY, useUnifiedLiquidity } from '@talos/kyoko';
import type { MarketSelectorItem } from '../types';
import { buildMarketSelectorItems, useBuildMarketSelectorItems } from '../utils/useBuildMarketSelectorItems';
import {
  getMarketDataMarketSelectorConditions,
  type GetMarketDataMarketSelectorConditionsParams,
  useMarketDataMarketSelectorConditions,
} from './useMarketDataMarketSelectorConditions';
import {
  getMarketDataMarketsAndAccounts,
  type GetMarketDataMarketsAndAccountsParams,
  useMarketDataMarketsAndAccounts,
  type UseMarketDataMarketsAndAccountsParams,
} from './useMarketDataMarketsAndAccounts';

interface UseMarketDataMarketSelectorItems extends UseMarketDataMarketsAndAccountsParams {
  queryMSSRecords?: boolean;
  /**
   * When false, skips symbol-driven markets/accounts and MSS-backed conditions so selector inventory stays cheap until needed.
   */
  buildItems?: boolean;
}

/**
 * Pulls together and builds the market selector items to be used by the market selector given some amount of use case
 * parameters.
 */
export const useMarketDataMarketSelectorItems = ({
  symbol,
  includeUnifiedLiquidityMarkets,
  queryMSSRecords = true,
  buildItems = true,
}: UseMarketDataMarketSelectorItems): MarketSelectorItem[] => {
  const effectiveSymbol = buildItems ? symbol : undefined;
  const effectiveIncludeUL = buildItems ? includeUnifiedLiquidityMarkets : false;
  const effectiveQueryMSS = buildItems ? queryMSSRecords : false;

  const ulResponse = useUnifiedLiquidity(effectiveIncludeUL ? effectiveSymbol : undefined);
  const { markets, marketAccounts } = useMarketDataMarketsAndAccounts({
    symbol: effectiveSymbol,
    includeUnifiedLiquidityMarkets: effectiveIncludeUL,
  });

  const { conditions } = useMarketDataMarketSelectorConditions({
    symbol: effectiveSymbol,
    ulTokensForSymbol: ulResponse?.Tokens,
    queryMSSRecords: effectiveQueryMSS,
  });

  const built = useBuildMarketSelectorItems({
    markets,
    marketAccounts,
    conditions,
    readyToBuildItems: buildItems,
  });

  if (!buildItems) {
    return EMPTY_ARRAY;
  }

  return built;
};

type GetMarketDataMarketSelectorItemsParams = GetMarketDataMarketsAndAccountsParams &
  Omit<GetMarketDataMarketSelectorConditionsParams, 'symbol' | 'ulTokensForSymbol'>;

export function getMarketDataMarketSelectorItems({
  security,
  securitiesBySymbol,
  enableMSSValidation,
  ulResponse,
  mssByAccountBySymbol,
  marketCredentialsWithConnectionStatusByMarketAccountName,
  includeUnifiedLiquidityMarkets,
  marketAccountsByMarket,
  marketsByName,
  permittedMarketAccountNames,
}: GetMarketDataMarketSelectorItemsParams) {
  const conditions = getMarketDataMarketSelectorConditions({
    enableMSSValidation,
    symbol: security?.Symbol,
    ulTokensForSymbol: ulResponse?.Tokens,
    securitiesBySymbol,
    mssByAccountBySymbol,
    marketCredentialsWithConnectionStatusByMarketAccountName,
  });

  const { markets, marketAccounts } = getMarketDataMarketsAndAccounts({
    security,
    includeUnifiedLiquidityMarkets,
    ulResponse,
    marketAccountsByMarket,
    marketsByName,
    permittedMarketAccountNames,
  });

  return buildMarketSelectorItems({ markets, marketAccounts, conditions, marketsByName });
}
